import React from 'react';
import {
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  MdArrowOutward,
  MdCalendarMonth,
  MdFolder,
  MdOutlineDirectionsCar,
  MdOutlineInventory2,
  MdOutlineQueryStats,
  MdOutlineToday,
  MdTimeline,
} from 'react-icons/md';
import IconBox from 'components/icons/IconBox';
import EmptyState from 'components/ui/EmptyState';
import SectionHeader from 'components/ui/SectionHeader';
import Surface from 'components/ui/Surface';
import useDashboardStats from 'hooks/useDashboardStats';
import useMonthFolders from 'hooks/useMonthFolders';
import useVehicleInspections from 'hooks/useVehicleInspections';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '-';

function MetricCard({ icon, label, value, helper, iconBg, iconColor }) {
  const helperColor = useColorModeValue(
    'secondaryGray.700',
    'secondaryGray.400',
  );
  const labelColor = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.300',
  );

  return (
    <Surface p="20px">
      <Stack spacing="16px">
        <HStack justify="space-between" align="flex-start">
          <IconBox
            w="48px"
            h="48px"
            borderRadius="18px"
            bg={iconBg}
            icon={<Icon as={icon} boxSize="22px" color={iconColor} />}
          />
          <Text
            textTransform="uppercase"
            letterSpacing="0.12em"
            fontSize="xs"
            color={labelColor}
            fontWeight="700"
          >
            {label}
          </Text>
        </HStack>
        <Box>
          <Heading size="2xl" lineHeight="1">
            {value}
          </Heading>
          <Text color={helperColor} mt="10px" fontSize="sm" lineHeight="1.7">
            {helper}
          </Text>
        </Box>
      </Stack>
    </Surface>
  );
}

function InspectionListItem({ inspection, onOpen, mutedText, brandColor }) {
  return (
    <Surface
      variant="muted"
      p="16px"
      cursor="pointer"
      transition="all .2s ease"
      _hover={{ transform: 'translateY(-2px)' }}
      onClick={onOpen}
    >
      <HStack justify="space-between" align="flex-start" spacing="12px">
        <Box minW="0">
          <Text fontWeight="800" noOfLines={1}>
            {inspection.inspectionNumber}
          </Text>
          <Text color={mutedText} fontSize="sm" mt="4px" noOfLines={1}>
            {inspection.vinNumber}
          </Text>
          <Text color={mutedText} fontSize="sm" mt="6px">
            {inspection.images?.length ?? 0} images
          </Text>
        </Box>
        <Stack spacing="6px" align="flex-end" flexShrink="0">
          <Icon as={MdArrowOutward} color={brandColor} boxSize="18px" />
          <Text color={mutedText} fontSize="xs">
            {formatDate(inspection.createdAt)}
          </Text>
        </Stack>
      </HStack>
    </Surface>
  );
}

export default function DashboardView() {
  const navigate = useNavigate();
  const brandColor = useColorModeValue('brand.500', 'brand.300');
  const accentBg = useColorModeValue(
    'rgba(111, 66, 193, 0.08)',
    'rgba(174, 134, 242, 0.12)',
  );
  const aquaBg = useColorModeValue(
    'rgba(13, 202, 240, 0.10)',
    'rgba(13, 202, 240, 0.12)',
  );
  const mutedText = useColorModeValue('secondaryGray.700', 'secondaryGray.400');
  const quickStatBg = useColorModeValue(
    'rgba(255,255,255,0.54)',
    'rgba(255,255,255,0.08)',
  );
  const quickStatBorder = useColorModeValue(
    'rgba(162, 176, 210, 0.14)',
    'rgba(255,255,255,0.08)',
  );
  const quickStatLabel = useColorModeValue(
    'secondaryGray.700',
    'secondaryGray.300',
  );
  const [search, setSearch] = React.useState('');
  const [year, setYear] = React.useState(new Date().getFullYear());

  const statsQuery = useDashboardStats();
  const foldersQuery = useMonthFolders(year);
  const recentQuery = useVehicleInspections({ page: 1, limit: 4, search });

  const stats = statsQuery.data?.data;
  const monthFolders = foldersQuery.data?.data || [];
  const availableYears = React.useMemo(
    () => foldersQuery.data?.availableYears || [],
    [foldersQuery.data?.availableYears],
  );
  const recentInspections = recentQuery.data?.data || [];

  React.useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(year)) {
      setYear(availableYears[0]);
    }
  }, [availableYears, year]);

  const primaryMetrics = [
    {
      label: 'Total Inspections',
      value: stats?.totalInspections ?? 0,
      helper: 'All inspections currently recorded in the admin system.',
      icon: MdOutlineInventory2,
      bg: accentBg,
    },
    {
      label: 'Created Today',
      value: stats?.todayInspections ?? 0,
      helper: 'New inspection records added by the team today.',
      icon: MdOutlineToday,
      bg: aquaBg,
    },
    {
      label: 'This Week',
      value: stats?.weeklyInspections ?? 0,
      helper: 'Weekly inspection volume to track current activity.',
      icon: MdOutlineQueryStats,
      bg: accentBg,
    },
    {
      label: 'This Month',
      value: stats?.monthlyInspections ?? 0,
      helper: 'Monthly inspection count for the active reporting period.',
      icon: MdCalendarMonth,
      bg: aquaBg,
    },
    {
      label: 'This Year',
      value: stats?.yearlyInspections ?? 0,
      helper: 'Year-to-date inspection volume across all stored records.',
      icon: MdTimeline,
      bg: accentBg,
    },
    {
      label: 'Unique VINs',
      value: stats?.uniqueVinNumbers ?? 0,
      helper: 'Distinct vehicles inspected across stored records.',
      icon: MdOutlineDirectionsCar,
      bg: aquaBg,
    },
  ];

  return (
    <Box pt={{ base: '130px', md: '120px', sm: '150px' }}>
      <Stack spacing="24px">
        <Surface variant="elevated" p={{ base: '22px', xl: '30px' }}>
          <Grid
            templateColumns={{ base: '1fr', xl: 'minmax(0, 1.4fr) 360px' }}
            gap="24px"
          >
            <Stack
              minW="0"
              spacing="14px"
              align={{ base: 'center', xl: 'flex-start' }}
              textAlign={{ base: 'center', xl: 'left' }}
            >
              <Text
                textTransform="uppercase"
                letterSpacing="0.18em"
                fontSize="xs"
                color={mutedText}
                fontWeight="700"
              >
                Operations Overview
              </Text>
              <Heading size="xl" maxW="820px">
                Vehicle Inspection Dashboard
              </Heading>
              <Text
                color={mutedText}
                fontSize="lg"
                maxW="760px"
                lineHeight="1.8"
              >
                Start from the high-level metrics, jump into monthly folders,
                and open recent inspections without the page feeling crowded.
              </Text>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                spacing="12px"
                w="100%"
                align={{ base: 'stretch', sm: 'flex-start' }}
                justify={{ base: 'center', xl: 'flex-start' }}
              >
                <Button
                  as={RouterLink}
                  to="/admin/inspections/create"
                  variant="brand"
                  w={{ base: '100%', md: 'auto' }}
                >
                  Create Inspection
                </Button>
                <Button
                  as={RouterLink}
                  to="/admin/inspections"
                  variant="subtle"
                  w={{ base: '100%', md: 'auto' }}
                >
                  Open Inspection List
                </Button>
              </Stack>
            </Stack>

            <Surface variant="muted" p="18px" minW="0">
              <SectionHeader
                title="Quick Access"
                description="Search directly or check the current month and year totals."
                mb="16px"
              />
              <Stack spacing="14px">
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search inspection or VIN"
                  variant="main"
                />
                <SimpleGrid columns={2} gap="12px">
                  <Box
                    p="16px"
                    borderRadius="20px"
                    bg={quickStatBg}
                    border="1px solid"
                    borderColor={quickStatBorder}
                  >
                    <Text color={quickStatLabel} fontSize="sm" mb="6px">
                      This Month
                    </Text>
                    <Heading size="lg">
                      {stats?.monthlyInspections ?? 0}
                    </Heading>
                  </Box>
                  <Box
                    p="16px"
                    borderRadius="20px"
                    bg={quickStatBg}
                    border="1px solid"
                    borderColor={quickStatBorder}
                  >
                    <Text color={quickStatLabel} fontSize="sm" mb="6px">
                      This Year
                    </Text>
                    <Heading size="lg">{stats?.yearlyInspections ?? 0}</Heading>
                  </Box>
                </SimpleGrid>
                <Button
                  variant="subtle"
                  onClick={() =>
                    navigate(
                      search
                        ? `/admin/inspections?search=${encodeURIComponent(search)}`
                        : '/admin/inspections',
                    )
                  }
                >
                  Search Records
                </Button>
              </Stack>
            </Surface>
          </Grid>
        </Surface>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap="18px">
          {primaryMetrics.map((metric) => (
            <Skeleton
              isLoaded={!statsQuery.isLoading}
              borderRadius="28px"
              key={metric.label}
            >
              <MetricCard
                icon={metric.icon}
                label={metric.label}
                value={metric.value}
                helper={metric.helper}
                iconBg={metric.bg}
                iconColor={brandColor}
              />
            </Skeleton>
          ))}
        </SimpleGrid>

        <Grid
          templateColumns={{ base: '1fr', xl: 'minmax(0, 1.7fr) 380px' }}
          gap="20px"
        >
          <Surface p={{ base: '22px', xl: '28px' }}>
            <SectionHeader
              title="Month-wise Folders"
              description="Browse inspections by month when you want to audit records in a structured way."
              action={
                <Select
                  maxW="170px"
                  variant="main"
                  value={year}
                  onChange={(event) => setYear(Number(event.target.value))}
                >
                  {(availableYears.length > 0 ? availableYears : [year]).map(
                    (option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ),
                  )}
                </Select>
              }
            />

            {foldersQuery?.isLoading ? (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="16px">
                {[1, 2, 3, 4].map((item) => (
                  <Skeleton key={item} h="132px" borderRadius="24px" />
                ))}
              </SimpleGrid>
            ) : monthFolders?.length === 0 ? (
              <EmptyState
                title="No month folders yet"
                description="Monthly groups will appear here once inspections have been created."
                minH="220px"
              />
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="16px">
                {monthFolders?.map((folder) => (
                  <Surface
                    key={`${folder.year}-${folder.month}`}
                    variant="muted"
                    p="18px"
                    cursor="pointer"
                    transition="all .2s ease"
                    _hover={{ transform: 'translateY(-3px)' }}
                    onClick={() =>
                      navigate(
                        `/admin/inspections?month=${folder.month}&year=${folder.year}`,
                      )
                    }
                  >
                    <HStack justify="space-between" align="flex-start">
                      <HStack spacing="14px" align="flex-start">
                        <IconBox
                          w="48px"
                          h="48px"
                          borderRadius="18px"
                          bg={accentBg}
                          icon={
                            <Icon
                              as={MdFolder}
                              boxSize="22px"
                              color={brandColor}
                            />
                          }
                        />
                        <Box>
                          <Text fontWeight="800" fontSize="lg">
                            {folder.monthName} {folder.year}
                          </Text>
                          <Text color={mutedText} mt="4px">
                            {folder.totalInspections} inspections
                          </Text>
                          <HStack spacing="6px" mt="10px" color={mutedText}>
                            <Icon as={MdCalendarMonth} boxSize="16px" />
                            <Text fontSize="sm">
                              Latest: {formatDate(folder.latestInspectionDate)}
                            </Text>
                          </HStack>
                        </Box>
                      </HStack>
                      <Icon
                        as={MdArrowOutward}
                        color={brandColor}
                        boxSize="20px"
                      />
                    </HStack>
                  </Surface>
                ))}
              </SimpleGrid>
            )}
          </Surface>

          <Surface p={{ base: '22px', xl: '28px' }}>
            <SectionHeader
              title="Recent Inspections"
              description="The latest activity, kept compact so the dashboard stays focused."
              action={
                <Text
                  as={RouterLink}
                  to="/admin/inspections"
                  color="brand.500"
                  fontWeight="800"
                >
                  View all
                </Text>
              }
            />
            <Skeleton isLoaded={!recentQuery.isLoading} borderRadius="24px">
              {recentInspections.length === 0 ? (
                <EmptyState
                  title="No recent inspections"
                  description="Create your first inspection to start populating this activity panel."
                  minH="220px"
                />
              ) : (
                <Stack spacing="12px">
                  {recentInspections.map((inspection) => (
                    <InspectionListItem
                      key={inspection.uuid}
                      inspection={inspection}
                      mutedText={mutedText}
                      brandColor={brandColor}
                      onOpen={() =>
                        navigate(`/admin/inspections/${inspection.uuid}`)
                      }
                    />
                  ))}
                </Stack>
              )}
            </Skeleton>
          </Surface>
        </Grid>
      </Stack>
    </Box>
  );
}
